import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  type KeyboardEvent,
} from "react";
import { useHistory } from "react-router-dom";

interface HistoryEntry {
  command: string;
  output: string[];
  isError?: boolean;
  timestamp: string;
  prompt: string;
}

interface GPUConfig {
  count: number;
  name: string;
  memTotal: number;
  powerCap: number;
}

interface VMConfig {
  name: string;
  user: string;
  id: string;
  machine: string;
  gpuConfig: GPUConfig;
  fileSystem: Record<string, string[]>;
  fileContents: Record<string, string>;
}

const WELCOME_MESSAGE = [
  "",
  "  Welcome to NVIDIA Brev",
  "  Type 'help' to see available commands.",
  "",
];

const LOCAL_FILE_SYSTEM: Record<string, string[]> = {
  "~": ["Desktop", "Documents", "Downloads", ".ssh", ".brev"],
  "~/Documents": ["notes.txt"],
  "~/Desktop": ["todo.txt"],
  "~/.ssh": ["known_hosts", "config"],
  "~/.brev": ["config.yaml"],
};

const LOCAL_FILE_CONTENTS: Record<string, string> = {
  "~/Documents/notes.txt":
    "Remember to push the latest changes before Friday.",
  "~/Desktop/todo.txt":
    "1. Set up GPU instance\n2. Train model\n3. Evaluate results",
  "~/.ssh/config":
    "Host brev-8xb200\n  HostName 10.0.0.42\n  User brev\n  IdentityFile ~/.ssh/brev_ed25519\n\nHost brev-4xh100\n  HostName 10.0.0.87\n  User brev\n  IdentityFile ~/.ssh/brev_ed25519\n\nHost brev-2xl40s\n  HostName 10.0.0.15\n  User brev\n  IdentityFile ~/.ssh/brev_ed25519",
  "~/.brev/config.yaml": "org_id: abc123\ndefault_instance: brev-8xb200",
};

const REMOTE_VMS: Record<string, VMConfig> = {
  "brev-8xb200": {
    name: "brev-8xb200",
    user: "brev",
    id: "9ve73bo2v",
    machine: "gb200-nvl72:nvidia-b200:8 (gpu)",
    gpuConfig: {
      count: 8,
      name: "NVIDIA B200",
      memTotal: 192000,
      powerCap: 1000,
    },
    fileSystem: {
      "~": ["workspace", "data", "checkpoints", "configs", ".config"],
      "~/workspace": [
        "pretrain.py",
        "data_pipeline.py",
        "eval_harness.py",
        "requirements.txt",
      ],
      "~/data": ["tokenized_corpus", "validation_set"],
      "~/data/tokenized_corpus": [
        "shard_0000.bin",
        "shard_0001.bin",
        "shard_0002.bin",
      ],
      "~/data/validation_set": ["eval_samples.jsonl"],
      "~/checkpoints": ["step_50000", "step_100000"],
      "~/configs": ["pretrain_config.yaml", "deepspeed_zero3.json"],
    },
    fileContents: {
      "~/workspace/pretrain.py":
        'import torch\nimport deepspeed\nfrom transformers import AutoModelForCausalLM, AutoConfig\n\nconfig = AutoConfig.from_pretrained("mistral-7b")\nconfig.num_hidden_layers = 80\nconfig.hidden_size = 8192\n\nmodel = AutoModelForCausalLM.from_config(config)\nengine, _, _, _ = deepspeed.initialize(\n    model=model,\n    config="configs/deepspeed_zero3.json"\n)\n\nprint(f"Model params: {sum(p.numel() for p in model.parameters()) / 1e9:.1f}B")\nprint("Pre-training started...")',
      "~/workspace/data_pipeline.py":
        'import numpy as np\nfrom torch.utils.data import IterableDataset\n\nclass ShardedDataset(IterableDataset):\n    def __init__(self, shard_dir, seq_length=4096):\n        self.shard_dir = shard_dir\n        self.seq_length = seq_length\n\n    def __iter__(self):\n        for shard in sorted(self.shard_dir.glob("*.bin")):\n            data = np.memmap(shard, dtype=np.uint16, mode="r")\n            for i in range(0, len(data) - self.seq_length, self.seq_length):\n                yield torch.from_numpy(data[i:i+self.seq_length].astype(np.int64))',
      "~/workspace/eval_harness.py":
        'from lm_eval import evaluator\nfrom lm_eval.models.huggingface import HFLM\n\nmodel = HFLM(pretrained="./checkpoints/step_100000")\nresults = evaluator.simple_evaluate(\n    model=model,\n    tasks=["hellaswag", "arc_challenge", "mmlu"],\n    batch_size=16,\n)\nfor task, metrics in results["results"].items():\n    print(f"{task}: {metrics}")',
      "~/workspace/requirements.txt":
        "torch==2.4.0\ndeepspeed==0.15.0\ntransformers==4.44.0\nnumpy==1.26.0\nlm-eval==0.4.3\nflash-attn==2.6.0",
      "~/configs/pretrain_config.yaml":
        "model_size: 70b\nseq_length: 4096\nbatch_size: 2\ngradient_accumulation_steps: 16\nlearning_rate: 1.5e-4\nwarmup_steps: 2000\nmax_steps: 200000\noptimizer: adamw\nweight_decay: 0.1\nfp16: false\nbf16: true",
      "~/configs/deepspeed_zero3.json":
        '{\n  "zero_optimization": {\n    "stage": 3,\n    "offload_param": { "device": "none" },\n    "offload_optimizer": { "device": "none" },\n    "overlap_comm": true,\n    "contiguous_gradients": true\n  },\n  "bf16": { "enabled": true },\n  "gradient_clipping": 1.0,\n  "train_micro_batch_size_per_gpu": 2,\n  "gradient_accumulation_steps": 16\n}',
      "~/data/validation_set/eval_samples.jsonl":
        '{"text": "The transformer architecture was introduced in"}\n{"text": "In distributed training, ZeRO Stage 3"}\n{"text": "Flash attention optimizes memory by"}',
    },
  },
  "brev-4xh100": {
    name: "brev-4xh100",
    user: "brev",
    id: "k2m48xp1q",
    machine: "a3-highgpu-4g:nvidia-h100-80gb-sxm:4 (gpu)",
    gpuConfig: {
      count: 4,
      name: "NVIDIA H100 80GB HBM3",
      memTotal: 81920,
      powerCap: 700,
    },
    fileSystem: {
      "~": ["workspace", "checkpoints", "data", "configs", ".config"],
      "~/workspace": ["finetune.py", "merge.py", "export.py", "requirements.txt"],
      "~/checkpoints": ["llama-3.1-70b-base", "llama-3.1-70b-lora-r32"],
      "~/data": ["instruction_pairs.jsonl", "dpo_pairs.jsonl"],
      "~/configs": ["finetune_config.yaml", "lora_config.json"],
    },
    fileContents: {
      "~/workspace/finetune.py":
        'from transformers import AutoModelForCausalLM, TrainingArguments\nfrom peft import LoraConfig, get_peft_model\nfrom trl import SFTTrainer\nfrom datasets import load_dataset\n\nmodel = AutoModelForCausalLM.from_pretrained(\n    "meta-llama/Llama-3.1-70B",\n    torch_dtype="bfloat16",\n    attn_implementation="flash_attention_2",\n)\nlora = LoraConfig(r=32, lora_alpha=64, target_modules="all-linear")\nmodel = get_peft_model(model, lora)\n\ndata = load_dataset("json", data_files="data/instruction_pairs.jsonl")\ntrainer = SFTTrainer(model=model, train_dataset=data["train"])\ntrainer.train()',
      "~/workspace/merge.py":
        'from peft import PeftModel\nfrom transformers import AutoModelForCausalLM\n\nbase = AutoModelForCausalLM.from_pretrained(\n    "meta-llama/Llama-3.1-70B",\n    torch_dtype="bfloat16"\n)\nmodel = PeftModel.from_pretrained(base, "./checkpoints/llama-3.1-70b-lora-r32")\nmerged = model.merge_and_unload()\nmerged.save_pretrained("./merged-llama-70b")\nprint("Merged model saved.")',
      "~/workspace/export.py":
        'from transformers import AutoModelForCausalLM, AutoTokenizer\n\nmodel = AutoModelForCausalLM.from_pretrained("./merged-llama-70b")\ntokenizer = AutoTokenizer.from_pretrained("meta-llama/Llama-3.1-70B")\n\nmodel.push_to_hub("my-org/llama-3.1-70b-finetuned")\ntokenizer.push_to_hub("my-org/llama-3.1-70b-finetuned")\nprint("Pushed to hub.")',
      "~/workspace/requirements.txt":
        "torch==2.4.0\ntransformers==4.44.0\npeft==0.12.0\ntrl==0.10.0\ndatasets==2.20.0\naccelerate==0.33.0\nflash-attn==2.6.0",
      "~/configs/finetune_config.yaml":
        "model_name: meta-llama/Llama-3.1-70B\nlora_r: 32\nlora_alpha: 64\nbatch_size: 1\ngradient_accumulation_steps: 16\nlearning_rate: 2e-4\nnum_epochs: 3\nmax_seq_length: 4096\nbf16: true",
      "~/configs/lora_config.json":
        '{\n  "r": 32,\n  "lora_alpha": 64,\n  "target_modules": "all-linear",\n  "lora_dropout": 0.05,\n  "bias": "none",\n  "task_type": "CAUSAL_LM"\n}',
      "~/data/instruction_pairs.jsonl":
        '{"instruction": "Explain how transformers work", "output": "Transformers use self-attention..."}\n{"instruction": "Write a Python quicksort", "output": "def quicksort(arr):..."}\n{"instruction": "What is RLHF?", "output": "Reinforcement Learning from Human Feedback..."}',
      "~/data/dpo_pairs.jsonl":
        '{"prompt": "Summarize this article", "chosen": "The article discusses...", "rejected": "idk lol"}\n{"prompt": "Debug this code", "chosen": "The issue is on line 12...", "rejected": "Looks fine to me"}',
    },
  },
  "brev-2xl40s": {
    name: "brev-2xl40s",
    user: "brev",
    id: "w7j92nt5r",
    machine: "g2-standard-8:nvidia-l40s:2 (gpu)",
    gpuConfig: {
      count: 2,
      name: "NVIDIA L40S",
      memTotal: 49152,
      powerCap: 350,
    },
    fileSystem: {
      "~": ["app", "models", "scripts", "logs", ".config"],
      "~/app": ["serve.py", "routes.py", "requirements.txt", "Dockerfile"],
      "~/models": ["llama-3.1-8b-awq", "embedding-model"],
      "~/models/llama-3.1-8b-awq": ["config.json", "model.safetensors"],
      "~/scripts": ["benchmark.py", "load_test.sh"],
      "~/logs": ["vllm.log", "access.log"],
    },
    fileContents: {
      "~/app/serve.py":
        'from vllm import LLM, SamplingParams\nfrom vllm.entrypoints.openai.api_server import run_server\n\nllm = LLM(\n    model="./models/llama-3.1-8b-awq",\n    quantization="awq",\n    tensor_parallel_size=2,\n    gpu_memory_utilization=0.90,\n    max_model_len=8192,\n)\n\nprint(f"Model loaded on {llm.llm_engine.parallel_config.tensor_parallel_size} GPUs")\nprint("vLLM server ready on :8000")',
      "~/app/routes.py":
        'from fastapi import FastAPI\nfrom pydantic import BaseModel\n\napp = FastAPI(title="Brev Inference API")\n\nclass CompletionRequest(BaseModel):\n    prompt: str\n    max_tokens: int = 256\n    temperature: float = 0.7\n\n@app.post("/v1/completions")\nasync def completions(req: CompletionRequest):\n    params = SamplingParams(max_tokens=req.max_tokens, temperature=req.temperature)\n    output = llm.generate([req.prompt], params)\n    return {"text": output[0].outputs[0].text}',
      "~/app/requirements.txt":
        "vllm==0.6.0\nfastapi==0.115.0\nuvicorn==0.30.0\nautoawq==0.2.6",
      "~/app/Dockerfile":
        'FROM nvidia/cuda:12.4.1-runtime-ubuntu22.04\nWORKDIR /app\nCOPY requirements.txt .\nRUN pip install -r requirements.txt\nCOPY . .\nEXPOSE 8000\nCMD ["python", "-m", "vllm.entrypoints.openai.api_server", "--model", "./models/llama-3.1-8b-awq", "--quantization", "awq", "--tensor-parallel-size", "2"]',
      "~/scripts/benchmark.py":
        'import time\nimport requests\n\nurl = "http://localhost:8000/v1/completions"\nprompts = [\n    "Explain quantum computing in simple terms",\n    "Write a haiku about machine learning",\n    "What is the capital of France?",\n]\n\nfor p in prompts:\n    start = time.time()\n    r = requests.post(url, json={"prompt": p, "max_tokens": 128})\n    elapsed = time.time() - start\n    tokens = len(r.json()["text"].split())\n    print(f"{tokens} tokens in {elapsed:.2f}s ({tokens/elapsed:.0f} tok/s)")',
      "~/logs/vllm.log":
        "INFO 02-06 07:30:00 llm_engine.py: Initializing vLLM engine v0.6.0\nINFO 02-06 07:30:05 llm_engine.py: Loading model llama-3.1-8b-awq (AWQ 4-bit)\nINFO 02-06 07:30:12 llm_engine.py: Tensor parallel across 2 GPUs\nINFO 02-06 07:30:12 llm_engine.py: Ready to serve requests",
      "~/logs/access.log":
        "2026-02-06 07:45:12 POST /v1/completions 200 128tok 340ms\n2026-02-06 07:45:13 POST /v1/completions 200 64tok 180ms\n2026-02-06 07:45:14 POST /v1/completions 200 256tok 620ms",
    },
  },
};

const VM_NAMES = Object.keys(REMOTE_VMS);

function buildNvidiaSmi(config: GPUConfig): string[] {
  const lines: string[] = [
    "Fri Feb  6 08:03:31 2026       ",
    "+-----------------------------------------------------------------------------------------+",
    "| NVIDIA-SMI 580.126.09             Driver Version: 580.126.09     CUDA Version: 13.0     |",
    "+-----------------------------------------+------------------------+----------------------+",
    "| GPU  Name                 Persistence-M | Bus-Id          Disp.A | Volatile Uncorr. ECC |",
    "| Fan  Temp   Perf          Pwr:Usage/Cap |           Memory-Usage | GPU-Util  Compute M. |",
    "|                                         |                        |               MIG M. |",
    "|=========================================+========================+======================|",
  ];

  for (let i = 0; i < config.count; i++) {
    const busSlot = String(i + 4).padStart(2, "0");
    const temp = 32 + i * 3;
    const pwr = Math.floor(config.powerCap * 0.02) + i;
    const memUsed = i * 2;

    const col1r1 = `   ${i}  ${config.name.padEnd(31)}On  `;
    const col2r1 = `   00000000:00:${busSlot}.0 Off `;
    const col3r1 = "                   0 ";
    lines.push(`|${col1r1}|${col2r1}|${col3r1}|`);

    const tempPrefix = ` N/A   ${temp}C    P8  `;
    const powerSuffix = `${pwr}W / ${String(config.powerCap).padStart(4)}W `;
    const powerPad = " ".repeat(41 - tempPrefix.length - powerSuffix.length);
    const col1r2 = `${tempPrefix}${powerPad}${powerSuffix}`;
    const col2r2 = `${String(memUsed).padStart(8)}MiB / ${String(
      config.memTotal
    ).padStart(6)}MiB `;
    const col3r2 = "      0%      Default ";
    lines.push(`|${col1r2}|${col2r2}|${col3r2}|`);

    lines.push(
      "|                                         |                        |                  N/A |"
    );
    lines.push(
      "+-----------------------------------------+------------------------+----------------------+"
    );
  }

  lines.push(
    "",
    "+-----------------------------------------------------------------------------------------+",
    "| Processes:                                                                              |",
    "|  GPU   GI   CI              PID   Type   Process name                        GPU Memory |",
    "|        ID   ID                                                               Usage      |",
    "|=========================================================================================|",
    "|  No running processes found                                                             |",
    "+-----------------------------------------------------------------------------------------+"
  );

  return lines;
}

function buildBrevList(): string[] {
  const vms = Object.values(REMOTE_VMS);
  const cols = { name: 22, status: 10, build: 12, shell: 8, id: 12 };
  const header = `${"NAME".padEnd(cols.name)}${"STATUS".padEnd(
    cols.status
  )}${"BUILD".padEnd(cols.build)}${"SHELL".padEnd(cols.shell)}${"ID".padEnd(
    cols.id
  )}MACHINE`;
  const rows = vms.map(
    (vm) =>
      `${vm.name.padEnd(cols.name)}${"RUNNING".padEnd(
        cols.status
      )}${"COMPLETED".padEnd(cols.build)}${"READY".padEnd(
        cols.shell
      )}${vm.id.padEnd(cols.id)}${vm.machine}`
  );
  return [
    `You have ${vms.length} instance${vms.length !== 1 ? "s" : ""} in Org my-org`,
    "",
    header,
    ...rows,
  ];
}

function getTimestamp(): string {
  return new Date().toLocaleTimeString("en-US", { hour12: false });
}

type CommandResult = {
  output: string[];
  isError?: boolean;
  shouldClear?: boolean;
  shouldConnect?: string;
  shouldDisconnect?: boolean;
  shouldNavigate?: string;
};

function processCommand(
  input: string,
  currentDir: string,
  setCurrentDir: (dir: string) => void,
  history: HistoryEntry[],
  fileSystem: Record<string, string[]>,
  fileContents: Record<string, string>,
  connectionState: "local" | "connecting" | "remote",
  activeVM: VMConfig | null
): CommandResult {
  const trimmed = input.trim();
  if (!trimmed) return { output: [] };

  const parts = trimmed.split(/\s+/);
  const cmd = parts[0]?.toLowerCase() || "";
  const args = parts.slice(1);

  switch (cmd) {
    case "help":
      return {
        output: [
          "",
          ...(connectionState === "local"
            ? [
                "  Try the Brev CLI (mock):",
                "",
                "  brev login           Log in to your Brev account",
                "  brev list            List available GPU instances",
                "  brev shell <name>    SSH into a Brev instance (try brev-8xb200!)",
                "",
              ]
            : []),
          connectionState === "local"
            ? "  Other commands:"
            : "  Available commands:",
          "",
          "  help              Show this help message",
          "  clear             Clear the terminal screen",
          "  echo <text>       Print text to the terminal",
          "  date              Display current date and time",
          "  whoami            Display current user",
          "  uname             Display system information",
          "  uptime            Display system uptime",
          "  pwd               Print working directory",
          "  ls [dir]          List directory contents",
          "  cd <dir>          Change directory",
          "  cat <file>        Display file contents",
          "  history           Show command history",
          ...(connectionState === "remote"
            ? [
                "  nvidia-smi        Display GPU information",
                "  exit              Disconnect from remote instance",
              ]
            : []),
          "",
        ],
      };

    case "clear":
      return { output: [], shouldClear: true };

    case "echo":
      return { output: [args.join(" ")] };

    case "date":
      return { output: [new Date().toString()] };

    case "whoami":
      return { output: [connectionState === "remote" ? "brev" : "user"] };

    case "uname":
      return { output: [connectionState === "remote" ? "Linux" : "Darwin"] };

    case "uptime": {
      const hours = Math.floor(Math.random() * 72) + 1;
      const mins = Math.floor(Math.random() * 60);
      return {
        output: [
          `up ${hours} hours, ${mins} minutes, 1 user, load average: 0.12, 0.08, 0.03`,
        ],
      };
    }

    case "pwd":
      return {
        output: [
          currentDir.replace(
            "~",
            connectionState === "remote" ? "/home/brev" : "/home/user"
          ),
        ],
      };

    case "ls": {
      const target = args[0] ? resolvePath(currentDir, args[0]) : currentDir;
      const contents = fileSystem[target];
      if (!contents) {
        return {
          output: [
            `ls: cannot access '${args[0] || "."}': No such file or directory`,
          ],
          isError: true,
        };
      }
      const formatted = contents.map((item) => {
        const fullPath = `${target}/${item}`;
        const isDir = fileSystem[fullPath] !== undefined;
        return isDir ? `${item}/` : item;
      });
      return { output: formatted };
    }

    case "cd": {
      if (!args[0] || args[0] === "~") {
        setCurrentDir("~");
        return { output: [] };
      }
      if (args[0] === "..") {
        const cdParts = currentDir.split("/");
        if (cdParts.length > 1) {
          cdParts.pop();
          const newDir = cdParts.join("/") || "~";
          setCurrentDir(newDir);
        }
        return { output: [] };
      }
      const resolved = resolvePath(currentDir, args[0]);
      if (fileSystem[resolved]) {
        setCurrentDir(resolved);
        return { output: [] };
      }
      return {
        output: [`cd: no such file or directory: ${args[0]}`],
        isError: true,
      };
    }

    case "cat": {
      if (!args[0]) {
        return { output: ["cat: missing operand"], isError: true };
      }
      const filePath = resolvePath(currentDir, args[0]);
      const content = fileContents[filePath];
      if (content) {
        return { output: content.split("\n") };
      }
      return {
        output: [`cat: ${args[0]}: No such file or directory`],
        isError: true,
      };
    }

    case "history":
      return {
        output: history.map(
          (entry, i) => `  ${String(i + 1).padStart(4, " ")}  ${entry.command}`
        ),
      };

    case "nvidia-smi": {
      if (connectionState !== "remote" || !activeVM) {
        return { output: ["command not found: nvidia-smi"], isError: true };
      }
      return { output: buildNvidiaSmi(activeVM.gpuConfig) };
    }

    case "brev": {
      if (args[0] === "login") {
        return {
          output: ["Opening login page..."],
          shouldNavigate: "/login",
        };
      }
      if (args[0] === "list" || args[0] === "ls") {
        if (connectionState === "remote") {
          return {
            output: ["brev: command not available in remote session"],
            isError: true,
          };
        }
        return { output: buildBrevList() };
      }
      if (args[0] === "shell" && args[1]) {
        if (connectionState === "remote") {
          return {
            output: [
              "Already connected to a remote instance. Use 'exit' to disconnect first.",
            ],
            isError: true,
          };
        }
        const alias = args[1];
        if (!REMOTE_VMS[alias]) {
          return {
            output: [
              `brev: instance '${alias}' not found`,
              "",
              "  Available instances:",
              ...Object.keys(REMOTE_VMS).map((name) => `    ${name}`),
            ],
            isError: true,
          };
        }
        return { output: [], shouldConnect: alias };
      }
      if (connectionState === "local") {
        return {
          output: [
            "",
            "  Usage:",
            "    brev login             Log in to your Brev account",
            "    brev list              List available instances",
            "    brev shell <name>      SSH into an instance",
            "",
            "  Available instances:",
            ...Object.keys(REMOTE_VMS).map((name) => `    ${name}`),
            "",
          ],
        };
      }
      return {
        output: ["brev: command not available in remote session"],
        isError: true,
      };
    }

    case "exit": {
      if (connectionState === "remote") {
        return { output: [], shouldDisconnect: true };
      }
      return {
        output: ["exit: not connected to any remote instance"],
        isError: true,
      };
    }

    default:
      return {
        output: [`command not found: ${cmd}`],
        isError: true,
      };
  }
}

function resolvePath(current: string, target: string): string {
  if (target.startsWith("~/") || target === "~") return target;
  return `${current}/${target}`;
}

function buildPrompt(
  connectionState: "local" | "connecting" | "remote",
  activeVM: VMConfig | null,
  currentDir: string
): string {
  if (connectionState === "remote" && activeVM) {
    return `${activeVM.user}@${activeVM.name}:${currentDir} $`;
  }
  return `${currentDir} $`;
}

export default function Terminal() {
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [input, setInput] = useState("");
  const [currentDir, setCurrentDir] = useState("~");
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [showWelcome, setShowWelcome] = useState(true);
  const [connectionState, setConnectionState] = useState<
    "local" | "connecting" | "remote"
  >("local");
  const [activeVM, setActiveVM] = useState<VMConfig | null>(null);
  const [pendingConnection, setPendingConnection] = useState<string | null>(
    null
  );
  const [hintIndex, setHintIndex] = useState(0);

  const routerHistory = useHistory();
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const fileSystem = activeVM ? activeVM.fileSystem : LOCAL_FILE_SYSTEM;
  const fileContents = activeVM ? activeVM.fileContents : LOCAL_FILE_CONTENTS;
  const prompt = buildPrompt(connectionState, activeVM, currentDir);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [history]);

  useEffect(() => {
    if (!showWelcome || input.length > 0 || connectionState !== "local") return;
    const interval = setInterval(() => {
      setHintIndex((prev) => (prev + 1) % VM_NAMES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [showWelcome, input, connectionState]);

  useEffect(() => {
    if (!pendingConnection) return;

    const vm = REMOTE_VMS[pendingConnection];
    if (!vm) return;

    const timer1 = setTimeout(() => {
      setHistory((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last) {
          updated[updated.length - 1] = {
            ...last,
            output: ["⣷ waiting for instance to be ready..."],
          };
        }
        return updated;
      });
    }, 100);

    const timer2 = setTimeout(() => {
      setHistory((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last) {
          updated[updated.length - 1] = {
            ...last,
            output: [
              "⣷ waiting for instance to be ready... done",
              "⣽ waiting for SSH connection to be available...",
            ],
          };
        }
        return updated;
      });
    }, 1100);

    const timer3 = setTimeout(() => {
      setHistory((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last) {
          updated[updated.length - 1] = {
            ...last,
            output: [
              "⣷ waiting for instance to be ready... done",
              "⣽ waiting for SSH connection to be available... done",
              "",
              `Connected to ${vm.name}`,
            ],
          };
        }
        return updated;
      });
      setActiveVM(vm);
      setCurrentDir("~");
      setConnectionState("remote");
      setPendingConnection(null);
    }, 2200);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [pendingConnection]);

  const focusInput = useCallback(() => {
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) return;
    inputRef.current?.focus({ preventScroll: true });
  }, []);

  const handleSubmit = useCallback(() => {
    if (connectionState === "connecting") return;

    const trimmed = input.trim();

    if (trimmed) {
      setCommandHistory((prev) => [...prev, trimmed]);
    }
    setHistoryIndex(-1);

    const result = processCommand(
      trimmed,
      currentDir,
      setCurrentDir,
      history,
      fileSystem,
      fileContents,
      connectionState,
      activeVM
    );

    if (result.shouldClear) {
      setHistory([]);
      setShowWelcome(false);
      setInput("");
      return;
    }

    if (result.shouldConnect) {
      setConnectionState("connecting");
      setHistory((prev) => [
        ...prev,
        {
          command: trimmed,
          output: [],
          timestamp: getTimestamp(),
          prompt,
        },
      ]);
      setPendingConnection(result.shouldConnect);
      setInput("");
      return;
    }

    if (result.shouldNavigate) {
      setHistory((prev) => [
        ...prev,
        {
          command: trimmed,
          output: result.output,
          timestamp: getTimestamp(),
          prompt,
        },
      ]);
      setInput("");
      setTimeout(() => routerHistory.push(result.shouldNavigate!), 500);
      return;
    }

    if (result.shouldDisconnect) {
      const vmName = activeVM?.name || "remote";
      setHistory((prev) => [
        ...prev,
        {
          command: trimmed,
          output: [`Connection to ${vmName} closed.`],
          timestamp: getTimestamp(),
          prompt,
        },
      ]);
      setActiveVM(null);
      setCurrentDir("~");
      setConnectionState("local");
      setInput("");
      return;
    }

    if (trimmed) {
      setHistory((prev) => [
        ...prev,
        {
          command: trimmed,
          output: result.output,
          isError: result.isError,
          timestamp: getTimestamp(),
          prompt,
        },
      ]);
    }

    setInput("");
  }, [
    input,
    currentDir,
    history,
    fileSystem,
    fileContents,
    connectionState,
    activeVM,
    prompt,
    routerHistory,
  ]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        handleSubmit();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (commandHistory.length > 0) {
          const newIndex =
            historyIndex === -1
              ? commandHistory.length - 1
              : Math.max(0, historyIndex - 1);
          setHistoryIndex(newIndex);
          setInput(commandHistory[newIndex] || "");
        }
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        if (historyIndex !== -1) {
          const newIndex = historyIndex + 1;
          if (newIndex >= commandHistory.length) {
            setHistoryIndex(-1);
            setInput("");
          } else {
            setHistoryIndex(newIndex);
            setInput(commandHistory[newIndex] || "");
          }
        }
      } else if (e.key === "Tab") {
        e.preventDefault();
        const cmds = [
          "help",
          "clear",
          "echo",
          "date",
          "whoami",
          "uname",
          "uptime",
          "pwd",
          "ls",
          "cd",
          "cat",
          "history",
          ...(connectionState === "local"
            ? ["brev"]
            : ["exit", "nvidia-smi"]),
        ];
        const match = cmds.filter((c) => c.startsWith(input.toLowerCase()));
        if (match.length === 1) {
          setInput(match[0] || "");
        }
      } else if (e.key === "l" && e.ctrlKey) {
        e.preventDefault();
        setHistory([]);
        setShowWelcome(false);
      }
    },
    [handleSubmit, commandHistory, historyIndex, input, connectionState]
  );

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `@keyframes hint-slide-up{from{transform:translateY(100%);opacity:0}to{transform:translateY(0);opacity:1}}`,
        }}
      />
      <div className="flex w-full items-center justify-center">
        <div
          className="border-border flex h-full w-full max-w-4xl flex-col overflow-hidden rounded-lg border"
          style={{ maxHeight: "320px", minHeight: "320px" }}
        >
          <div className="border-border flex items-center justify-between border-b px-4 py-2.5">
            <div className="flex items-center gap-2">
              <div className="flex gap-1.5">
                <span className="block h-3 w-3 rounded-full bg-[hsl(0,0%,25%)] transition-colors hover:bg-[hsl(0,70%,55%)]" />
                <span className="block h-3 w-3 rounded-full bg-[hsl(0,0%,25%)] transition-colors hover:bg-[hsl(45,80%,55%)]" />
                <span className="block h-3 w-3 rounded-full bg-[hsl(0,0%,25%)] transition-colors hover:bg-[hsl(120,50%,45%)]" />
              </div>
            </div>
            <span className="text-muted-foreground text-xs select-none">
              {connectionState === "remote" && activeVM
                ? `${activeVM.user}@${activeVM.name}: ${currentDir}`
                : `terminal: ${currentDir}`}
            </span>
            <div className="w-[52px]" />
          </div>

          <div
            ref={scrollRef}
            className="flex-1 cursor-text overflow-y-auto p-4 font-mono select-text focus:outline-none"
            onMouseUp={focusInput}
            role="log"
            aria-label="Terminal output"
            aria-live="polite"
          >
            {showWelcome && (
              <div className="mb-2">
                {WELCOME_MESSAGE.map((line) => (
                  <div
                    key={line || "empty"}
                    className="text-muted-foreground text-sm leading-6 whitespace-pre"
                  >
                    {line || "\u00A0"}
                  </div>
                ))}
              </div>
            )}

            {history.map((entry) => (
              <div key={entry.timestamp} className="mb-1">
                <div className="flex items-center gap-2 text-sm leading-6">
                  <span className="text-foreground shrink-0 select-none">
                    {entry.prompt}
                  </span>
                  <span className="text-[#76B900]">{entry.command}</span>
                </div>
                {Array.from(entry.output, (line, i) => ({
                  line,
                  key: `${entry.timestamp}-output-${i}`,
                })).map(({ line, key }) => (
                  <div
                    key={key}
                    className={`text-sm leading-6 whitespace-pre ${
                      entry.isError
                        ? "text-muted-foreground"
                        : "text-foreground/70"
                    }`}
                  >
                    {line || "\u00A0"}
                  </div>
                ))}
              </div>
            ))}

            {connectionState !== "connecting" && (
              <div className="flex items-center gap-2 text-sm leading-6">
                <span className="text-foreground shrink-0 select-none">
                  {prompt}
                </span>
                <div className="relative flex-1">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="w-full border-0 bg-transparent p-0 text-sm text-[#76B900] caret-[#76B900] shadow-none outline-none ring-0 focus:border-0 focus:outline-none focus:ring-0 focus-visible:outline-none appearance-none"
                    style={{ boxShadow: "none" }}
                    spellCheck={false}
                    autoComplete="off"
                    autoFocus
                    aria-label="Terminal input"
                  />
                  {showWelcome &&
                    input.length === 0 &&
                    connectionState === "local" &&
                    history.length === 0 && (
                      <span className="text-muted-foreground/40 pointer-events-none absolute inset-0 flex items-center text-sm">
                        try &apos;brev shell{" "}
                        <span
                          className="ml-[10px] inline-flex items-center overflow-hidden"
                          style={{ height: "1.5em" }}
                        >
                          <span
                            key={hintIndex}
                            className="inline-block"
                            style={{ animation: "hint-slide-up 0.4s ease-out" }}
                          >
                            {VM_NAMES[hintIndex]}
                          </span>
                        </span>
                        &apos;
                      </span>
                    )}
                </div>
              </div>
            )}
          </div>

          <div className="border-border flex items-center justify-between border-t px-4 py-1.5">
            <div className="flex items-center gap-3">
              <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
                <span
                  className={`block h-1.5 w-1.5 rounded-full ${
                    connectionState === "remote"
                      ? "bg-[hsl(120,50%,45%)]"
                      : connectionState === "connecting"
                        ? "bg-[hsl(45,80%,55%)]"
                        : "bg-[hsl(0,0%,40%)]"
                  }`}
                />
                {connectionState === "remote" && activeVM
                  ? `SSH: ${activeVM.name}`
                  : connectionState === "connecting"
                    ? "SSH: connecting..."
                    : "local"}
              </span>
            </div>
            <div className="text-muted-foreground flex items-center gap-3 text-xs">
              <span>UTF-8</span>
              <span>LF</span>
              <span>{connectionState === "remote" ? "/bin/bash" : "/bin/zsh"}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
